/**
 * First-hour quest fixes F1-F3 (docs/design/quests/2026-10-11-first-hour-f1-f4.md), run for systems/plainsStory.ts:
 *
 * - **F1 lockForm.** The first duel also asks for the lock form 蘆 (ท่าล็อกเป้า). With the duel clone's lock rules on
 *   (`DUEL_FLAGS.v2`) it counts when the player's move starts a locked form (systems/duelLock.ts); with them off the form
 *   cannot start, so the move's landing hit counts instead (the quest must never be stuck).
 * - **F2 route.** `ROUTES.stones`: checkpoints reached in order over the stream; a fall into deep water outside a
 *   checkpoint sends the hero back to the west bank. Needs the first qinggong art (`needsArt`).
 * - **F3 escort.** A cart (an NPC entity) held by three guards at the bandit hill. It rolls along `ESCORTS.cart.path`
 *   while the hero leads (within `leadRange`) and no guard or wave man is near it; waves spawn at set path indices; wave
 *   men near the cart wear its hp down; at 0 the cart and the wave reset, the quest stays.
 *
 * Deterministic: fixed offsets, time only from `step(dt)`. The marker (`world.story.guide`) points at the next checkpoint or the
 * cart's next stop; the cart's hp is the entity's hp, so the target frame shows it.
 */
import { ESCORTS, ESCORT_RULES, LINES, PQ, ROUTES, type EscortDef } from '../data/plainsOpening';
import { DUEL_FLAGS } from '../data/duelMatch';
import { nearestFloor } from '../path';
import { waterDepthAt } from '../qinggong';
import { duelLockOf } from './duelLock';
import type { ObjectiveType } from '../data/quests';
import type { Entity, WorldEvent } from '../world';
import { dist } from '../world';
import type { SystemHost } from './types';

/** What the helper needs of the plains story. */
export interface FQuestCtx {
  readonly h: SystemHost;
  at(id: string, status?: 'available' | 'active' | 'ready'): boolean;
  bump(type: ObjectiveType, target?: string): void;
  bark(id: number, text: string, after?: number): void;
  sex(): 'm' | 'f';
}

/** The cart as the client may read it. */
export interface EscortView {
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  /** Path index the cart has reached, and the path's last index. */
  idx: number;
  last: number;
  /** Held in place (guards or wave men near, or the hero too far). */
  held: boolean;
}

export class FirstHourQuests {
  private lockPending = false;
  private cart: Entity | null = null;
  private def: EscortDef = ESCORTS.cart!;
  private idx = 0;
  private hp = 0;
  private held = false;
  private done = false;
  private guards: Entity[] = [];
  private wave: Entity[] = [];
  private wavesDone = new Set<number>();
  private hurtT = 0;
  private tauntT = 0;
  private needArtSaid = false;

  constructor(private readonly c: FQuestCtx) {}

  /** The cart's state, or null when no escort is under way. */
  get escort(): EscortView | null {
    const e = this.cart;
    if (!e || this.done) return null;
    return { x: e.x, y: e.y, hp: this.hp, maxHp: this.def.cartHp, idx: this.idx, last: this.def.path.length - 1, held: this.held };
  }

  // ───────────── events ─────────────

  observe(ev: WorldEvent): void {
    const w = this.c.h.world;
    if (ev.type !== 'hit' || ev.srcId !== w.player.id || !this.c.at(PQ.duel, 'active')) return;
    if (ev.skillId !== 'bs-reed' || (ev.outcome !== 'hit' && ev.outcome !== 'blocked')) return;
    if (DUEL_FLAGS.v2) this.lockPending = true;
    else this.c.bump('lockForm', 'bs-reed');
  }

  // ───────────── step ─────────────

  step(dt: number): void {
    const w = this.c.h.world;
    if (this.lockPending) {
      this.lockPending = false;
      const f = duelLockOf(w)?.formOf(w.player.id);
      if (f && f[0] === w.player.id) this.c.bump('lockForm', 'bs-reed');
    }
    if (this.c.at(PQ.stones, 'active')) this.stepRoute();
    this.stepEscort(dt);
  }

  private routeIndex(): number {
    const q = this.c.h.world.currentQuest();
    return q ? q.objectives.findIndex((o) => o.type === 'route') : -1;
  }

  private stepRoute(): void {
    const w = this.c.h.world;
    const p = w.player;
    const r = ROUTES.stones!;
    const oi = this.routeIndex();
    if (oi < 0 || p.dead) return;
    const qs = w.progress.quest;
    const cur = qs.progress[oi] ?? 0;
    const next = r.points[Math.min(cur, r.points.length - 1)]!;
    w.story.guide = { x: next.at[0], y: next.at[1] };
    if ((w.progress.qinggong[r.needsArt] ?? 0) < 1) {
      if (!this.needArtSaid && dist(p, { x: r.points[0]!.at[0], y: r.points[0]!.at[1] }) < 14) {
        this.needArtSaid = true;
        this.c.h.notice(LINES.f2NeedArt);
      }
      return;
    }
    // Fell into the stream away from a checkpoint: back to the bank (the climb out is the fall's own cost).
    const onPoint = r.points.some((pt) => Math.hypot(p.x - pt.at[0], p.y - pt.at[1]) < pt.r);
    if (cur > 0 && !onPoint && waterDepthAt(w.map, p.x, p.y) > 0.8) {
      p.x = r.resetTo[0];
      p.y = r.resetTo[1];
      qs.progress[oi] = 0;
      this.c.bark(p.id, LINES.f2Fall);
      this.c.h.emit({ type: 'quest', status: 'progress', questId: PQ.stones });
      return;
    }
    if (cur < r.points.length && Math.hypot(p.x - next.at[0], p.y - next.at[1]) < next.r) this.c.bump('route', 'stones');
  }

  // ───────────── F3 ─────────────

  private floor(x: number, y: number): { x: number; y: number } {
    const f = nearestFloor(this.c.h.world.map, x, y, 4);
    return f ? { x: f[0], y: f[1] } : { x, y };
  }

  private alive(list: Entity[]): Entity[] {
    const w = this.c.h.world;
    return list.filter((e) => w.entities.has(e.id) && !e.dead);
  }

  private spawn(id: string, x: number, y: number): Entity {
    const at = this.floor(x, y);
    const m = this.c.h.spawnMonster(id, at.x, at.y);
    return m;
  }

  private chase(m: Entity): void {
    const p = this.c.h.world.player;
    if (m.brain && m.brain.state !== 'chase') {
      m.brain.state = 'chase';
      m.brain.foeId = p.id;
      m.targetId = p.id;
    }
  }

  private escortObjective(): number {
    const q = this.c.h.world.currentQuest();
    return q ? q.objectives.findIndex((o) => o.type === 'escort') : -1;
  }

  private startEscort(): void {
    const w = this.c.h.world;
    const d = this.def;
    const at = this.floor(d.from[0], d.from[1]);
    this.cart = this.c.h.add({
      kind: 'npc',
      x: at.x,
      y: at.y,
      name: 'เกวียนของลุงเกา',
      title: 'พาถึงประตูเนินโจร',
      sprite: 'stationMaster',
      facing: 1,
    });
    this.cart.maxHp = d.cartHp;
    this.cart.hp = d.cartHp;
    this.idx = 0;
    this.hp = d.cartHp;
    this.done = false;
    this.wavesDone.clear();
    this.wave = [];
    this.fillGuards();
    void w;
  }

  /** Guards enough for the kills still owed (never more than `guards` at once). */
  private fillGuards(): void {
    const w = this.c.h.world;
    const q = w.currentQuest();
    const ki = q ? q.objectives.findIndex((o) => o.type === 'kill') : -1;
    const owed = ki >= 0 ? q!.objectives[ki]!.count - (w.progress.quest.progress[ki] ?? 0) : ESCORT_RULES.guards;
    this.guards = this.alive(this.guards);
    const want = Math.min(ESCORT_RULES.guards, Math.max(0, owed));
    const offs: readonly (readonly [number, number])[] = [
      [2.5, -1],
      [-2.5, -1],
      [0, 3],
    ];
    const from = this.def.from;
    for (let k = this.guards.length; k < want; k++) {
      const o = offs[k % offs.length]!;
      this.guards.push(this.spawn(ESCORT_RULES.guardMonster, from[0] + o[0], from[1] + o[1]));
    }
  }

  private stepEscort(dt: number): void {
    const w = this.c.h.world;
    const p = w.player;
    const active = this.c.at(PQ.bandits, 'active') && this.escortObjective() >= 0;
    if (!active) {
      this.held = false;
      return;
    }
    const oi = this.escortObjective();
    if (!this.cart && (w.progress.quest.progress[oi] ?? 0) < 1) this.startEscort();
    const cart = this.cart;
    if (!cart || this.done) return;
    this.tauntT = Math.max(0, this.tauntT - dt);
    this.hurtT = Math.max(0, this.hurtT - dt);
    this.guards = this.alive(this.guards);
    this.wave = this.alive(this.wave);
    const threats = [...this.guards, ...this.wave];
    for (const m of threats) if (dist(m, p) < 14) this.chase(m);
    if (this.guards.length && dist(p, cart) < 12 && this.tauntT <= 0) {
      this.tauntT = 14;
      this.guards.forEach((g, k) => k < 2 && this.c.bark(g.id, LINES.f3Guard[k]!, k * 2.4));
    }
    // Wave men near the cart wear it down.
    const hitters = threats.filter((m) => dist(m, cart) < ESCORT_RULES.hurtRange && !m.stunT);
    if (hitters.length && !p.dead) {
      this.hp -= hitters.length * ESCORT_RULES.hurtPerSec * dt;
      if (this.hurtT <= 0) {
        this.hurtT = 6;
        this.c.bark(cart.id, LINES.f3CartHit);
      }
    }
    cart.hp = Math.max(1, this.hp);
    if (this.hp <= 0) return this.reset();
    // Held while any foe is near the cart, or the hero is not leading.
    const foeNear = threats.some((m) => dist(m, cart) < ESCORT_RULES.holdRange);
    const leading = !p.dead && dist(p, cart) < ESCORT_RULES.leadRange;
    this.held = foeNear || !leading;
    const path = this.def.path;
    const to = path[Math.min(this.idx + 1, path.length - 1)]!;
    w.story.guide = this.held && foeNear ? { x: cart.x, y: cart.y } : { x: to[0], y: to[1] };
    if (this.held) return;
    const dx = to[0] - cart.x;
    const dy = to[1] - cart.y;
    const d = Math.hypot(dx, dy);
    const step = this.def.speed * dt;
    if (d > step) {
      cart.x += (dx / d) * step;
      cart.y += (dy / d) * step;
      cart.facing = dx >= 0 ? 1 : -1;
      return;
    }
    cart.x = to[0];
    cart.y = to[1];
    this.idx = Math.min(this.idx + 1, path.length - 1);
    for (const wv of this.def.waves) {
      if (wv.atPathIndex !== this.idx || this.wavesDone.has(wv.atPathIndex)) continue;
      this.wavesDone.add(wv.atPathIndex);
      const line = LINES.f3Wave[this.wavesDone.size - 1] ?? LINES.f3Wave[0];
      for (let k = 0; k < wv.n; k++) {
        const side = k % 2 === 0 ? 1 : -1;
        const m = this.spawn(wv.monsters, cart.x + side * 5, cart.y + 2 + k);
        this.wave.push(m);
        this.chase(m);
        if (k === 0) this.c.bark(m.id, line);
      }
    }
    if (this.idx >= path.length - 1) {
      this.done = true;
      this.c.bark(cart.id, LINES.f3Done);
      this.c.bump('escort', 'cart');
    }
  }

  /** The cart is wrecked: back to the start, the wave gone, the guards back for the kills still owed. */
  private reset(): void {
    const w = this.c.h.world;
    const cart = this.cart!;
    for (const m of this.wave) w.entities.delete(m.id);
    this.wave = [];
    this.wavesDone.clear();
    const at = this.floor(this.def.failTo[0], this.def.failTo[1]);
    cart.x = at.x;
    cart.y = at.y;
    this.idx = 0;
    this.hp = this.def.cartHp;
    cart.hp = this.hp;
    this.fillGuards();
    this.c.h.notice(LINES.f3Reset);
    this.c.h.log('sys', LINES.f3Reset);
  }
}
